/**
 * Vault — the self-custody wallet (Phase 9, spec §6.4, D60–D64).
 *   no vault  → welcome: create or import (+ browser warning)
 *   locked    → unlock with the vault password
 *   unlocked  → aurora hero with the vault's value, assets (on-chain balances of the PUBLIC
 *               addresses, read through our API), addresses, and security actions.
 * Send (9b) signs on the device; vault swaps arrive with mainnet swaps (D64).
 */
import type { VaultBalanceRow } from "@custos/api-client";
import {
  Address,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  Field,
  Notice,
  Skeleton,
  Spot,
  buttonClasses,
  cn,
  formatAmount,
  formatDateTime,
  formatFiat,
  isZeroAmount,
} from "@custos/ui";
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, KeyRound, Lock, Plus, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { PageHeader } from "../components/AppShell";
import { AssetIcon, ExternalLink } from "../components/bits";
import {
  AddKeyModal,
  ChangePasswordModal,
  RemoveVaultModal,
  RevealSecretModal,
} from "../components/VaultModals";
import { BrowserVaultWarning, SecretInput } from "../components/VaultBits";
import { VaultRecoveryCard } from "../components/VaultRecoveryCard";
import { findChain, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { usePrefs } from "../lib/prefs";
import { useConfig, useVaultBalances } from "../lib/queries";
import { useVault } from "../lib/vault";
import { FAMILY_NAME, familyChains, useVaultSession } from "../lib/vault-ui";
import { useVaultSends } from "../lib/vault-sends";
import { explorerTxUrl } from "../lib/chains";

export function VaultPage() {
  const vault = useVaultSession();
  switch (vault.status) {
    case "loading":
      return (
        <>
          <PageHeader title="Vault" />
          <Skeleton className="h-64 rounded-card" />
        </>
      );
    case "unavailable":
      return (
        <>
          <PageHeader title="Vault" />
          <Notice tone="danger" title="This browser blocks local storage">
            The vault needs to store its encrypted data in this browser. Private/incognito windows and some
            privacy settings block that. Use a normal window to use the vault.
          </Notice>
        </>
      );
    case "none":
      return <VaultWelcome />;
    case "locked":
      return <VaultUnlock />;
    case "unlocked":
      return <VaultDashboard />;
  }
}

function VaultWelcome() {
  return (
    <>
      <PageHeader title="Vault" subtitle="Your self-custody wallet." />
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <section className="bg-aurora animate-rise flex flex-col items-center gap-4 rounded-[32px] p-6 text-center sm:p-10">
          <Spot name="vault" className="size-24" />
          <h2 className="text-3xl font-normal tracking-tight">Your keys. Your device.</h2>
          <p className="max-w-md text-white/85">
            Import a recovery phrase or private key, or create a new wallet. It's encrypted with your vault
            password and <b>never leaves this device unencrypted</b>. Recovery is on by default: an encrypted
            backup is kept that only the Aurix recovery key — held offline by one person — can open, so we can
            restore your vault if you lose your phrase and device. You can turn it off.
          </p>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Link
              to="/vault/create"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-6 font-medium text-[#0B0A10] transition-transform active:scale-95"
            >
              <Plus className="size-5" aria-hidden /> Create new vault
            </Link>
            <Link
              to="/vault/import"
              className="glass-circle inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-medium"
            >
              <KeyRound className="size-5" aria-hidden /> Import existing wallet
            </Link>
          </div>
        </section>
        <BrowserVaultWarning />
        <Link to="/vault/recover" className="self-center text-sm text-primary-strong hover:underline">
          Lost your recovery phrase and device? Recover your vault with support
        </Link>
        <Card>
          <CardTitle>Vault or Account?</CardTitle>
          <ul className="mt-2 list-disc pl-5 text-sm text-muted">
            <li>
              <b className="text-fg">Account</b> — custodial: we hold the keys; deposits, withdrawals and
              swaps go through the service.
            </li>
            <li>
              <b className="text-fg">Vault</b> — self-custody: only you hold the keys, on this device. Lose
              the recovery phrase and the funds are gone.
            </li>
          </ul>
        </Card>
      </div>
    </>
  );
}

function VaultUnlock() {
  const unlock = useVault((s) => s.unlock);
  const families = useVault((s) => s.families);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unlock(password);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Vault" />
      <Card className="animate-rise mx-auto flex w-full max-w-md flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="bg-aurora inline-flex size-12 items-center justify-center rounded-full">
            <Lock className="size-5" aria-hidden />
          </span>
          <div>
            <CardTitle>Vault locked</CardTitle>
            <p className="text-sm text-muted">{families.map((f) => FAMILY_NAME[f]).join(" · ")}</p>
          </div>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field label="Vault password">
            {(p) => (
              <SecretInput
                {...p}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
            )}
          </Field>
          {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
          <Button type="submit" block loading={busy} disabled={!password}>
            Unlock
          </Button>
        </form>
        <button
          type="button"
          onClick={() => setRemoveOpen(true)}
          className="min-h-11 text-sm text-muted hover:text-fg hover:underline"
        >
          Forgot the password? Remove the vault and import your recovery phrase again.
        </button>
        <Link to="/vault/recover" className="text-center text-sm text-primary-strong hover:underline">
          Lost the recovery phrase too? Recover with support
        </Link>
      </Card>
      <RemoveVaultModal open={removeOpen} onOpenChange={setRemoveOpen} />
    </>
  );
}

function VaultDashboard() {
  const config = useConfig();
  const chains = config.data?.chains ?? [];
  const currency = usePrefs((s) => s.currency);
  const autoLock = usePrefs((s) => s.vaultAutoLockMinutes);
  const { accounts, kind, families, lock } = useVault();
  const balances = useVaultBalances(accounts, currency, true);
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState<null | "reveal" | "password" | "add" | "remove">(null);
  const close = (open: boolean) => !open && setDialog(null);

  const rows = balances.data?.assets ?? [];
  const shownRows = showAll ? rows : rows.filter((r) => !r.available || !isZeroAmount(r.balance ?? "0"));
  const data = balances.data;
  const missingFamilies = kind === "privateKeys" && families.length < 4;

  return (
    <>
      <div className="lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:overflow-hidden lg:rounded-[32px] lg:bg-surface">
        <section
          aria-label="Vault"
          className="bg-aurora animate-rise relative overflow-hidden rounded-[32px] p-5 sm:p-8 lg:rounded-none lg:p-10"
        >
          <div className="flex items-center justify-between gap-3">
            <h1 className="flex items-center gap-2 text-[22px] font-normal sm:text-2xl">
              <ShieldCheck className="size-6" aria-hidden /> Vault
            </h1>
            <button
              type="button"
              onClick={lock}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-medium text-[#0B0A10] transition-transform active:scale-95"
            >
              <Lock className="size-4" aria-hidden /> Lock
            </button>
          </div>
          <div className="mt-8 text-center lg:mt-12 lg:text-left">
            <p className="text-base text-white/85 sm:text-lg">Self-custody ({currency})</p>
            <p
              className="mt-1 text-6xl leading-none font-normal tracking-[-0.04em] sm:text-7xl"
              data-testid="vault-total"
            >
              {data && (data.complete || data.total !== "0.00") ? formatFiat(data.total, currency) : "—"}
            </p>
            {data && !data.complete && (
              <span className="mt-3 inline-flex rounded-full bg-white/12 px-3 py-1 text-sm text-white/90 backdrop-blur">
                Some balances or prices are unavailable right now
              </span>
            )}
          </div>
          <nav
            aria-label="Vault actions"
            className="mt-8 flex items-center justify-center gap-2.5 lg:justify-start"
          >
            <Link
              to="/vault/send"
              aria-label="Send"
              title="Send"
              className="glass-circle inline-flex size-14 items-center justify-center rounded-full transition-transform active:scale-95 lg:size-16"
            >
              <ArrowUpRight className="size-6" aria-hidden />
            </Link>
            <Link
              to="/vault/receive"
              aria-label="Receive"
              title="Receive"
              className="glass-circle inline-flex size-14 items-center justify-center rounded-full transition-transform active:scale-95 lg:size-16"
            >
              <ArrowDownLeft className="size-6" aria-hidden />
            </Link>
            <span
              title="Vault swaps arrive together with mainnet swaps"
              aria-label="Swap (coming with mainnet swaps)"
              role="img"
              className="glass-circle inline-flex size-14 items-center justify-center rounded-full opacity-50 lg:size-16"
            >
              <ArrowLeftRight className="size-6" aria-hidden />
            </span>
          </nav>
          <p className="mt-4 text-center text-xs text-white/75 lg:text-left">
            Locks after {autoLock} min without activity · Swap: with mainnet swaps
          </p>
        </section>

        <section aria-label="Vault assets" className="mt-5 flex flex-col gap-4 lg:mt-0 lg:p-8">
          <p className="-mb-1 text-xs text-muted">Self-custody — keys only on this device.</p>
          {balances.isPending ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-20 rounded-card" />
              ))}
            </div>
          ) : balances.error ? (
            <Notice tone="danger">{errorMessage(balances.error)}</Notice>
          ) : shownRows.length === 0 ? (
            <div className="glass rounded-card border border-line lg:bg-soft">
              <EmptyState icon={<Spot name="coins" />} title="No funds in the vault yet">
                <Link to="/vault/receive" className="text-primary-strong hover:underline">
                  Show my vault addresses
                </Link>
              </EmptyState>
            </div>
          ) : (
            <ul className="stagger flex flex-col gap-3" data-testid="vault-assets">
              {shownRows.map((r) => (
                <VaultAssetRow
                  key={`${r.chain}:${r.asset}`}
                  row={r}
                  currency={currency}
                  network={networkLabel(findChain(chains, r.chain), r.chain)}
                />
              ))}
            </ul>
          )}
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mx-auto min-h-11 rounded-full px-4 text-sm text-fg/90 hover:bg-hover"
            >
              {showAll ? "Show less" : "Show all assets"}
            </button>
          )}
        </section>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardTitle>Your vault addresses</CardTitle>
          <p className="mt-1 text-sm text-muted">Public — safe to share for receiving.</p>
          <ul className="mt-4 flex flex-col gap-3">
            {accounts.map((a) => (
              <li key={a.family} className="rounded-field border border-line bg-soft p-3">
                <p className="text-sm font-medium text-fg">{FAMILY_NAME[a.family]}</p>
                <p className="text-xs text-muted">{familyChains(chains, a.family)}</p>
                <div className="mt-2" data-testid={`vault-address-${a.family}`}>
                  <Address value={a.address} copy />
                </div>
                {a.path && <p className="mt-1 font-mono text-xs text-muted">{a.path}</p>}
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <CardTitle>Security</CardTitle>
          <p className="mt-1 text-sm text-muted">
            Auto-lock after {autoLock} minutes —{" "}
            <Link to="/settings" className="text-primary-strong hover:underline">
              change in Settings
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <Button variant="secondary" block onClick={() => setDialog("reveal")}>
              {kind === "mnemonic" ? "Show recovery phrase" : "Show private keys"}
            </Button>
            <Button variant="secondary" block onClick={() => setDialog("password")}>
              Change vault password
            </Button>
            {missingFamilies && (
              <Button variant="secondary" block onClick={() => setDialog("add")}>
                Add a private key for another chain
              </Button>
            )}
            <Button variant="ghost" block className="text-danger" onClick={() => setDialog("remove")}>
              Remove vault from this device
            </Button>
          </div>
        </Card>
      </div>

      <RecentSends />
      <VaultRecoveryCard />

      <RevealSecretModal open={dialog === "reveal"} onOpenChange={close} />
      <ChangePasswordModal open={dialog === "password"} onOpenChange={close} />
      <AddKeyModal open={dialog === "add"} onOpenChange={close} />
      <RemoveVaultModal open={dialog === "remove"} onOpenChange={close} />
    </>
  );
}

/** Sends made from this vault in THIS browser (public data only, D66). */
function RecentSends() {
  const config = useConfig();
  const sends = useVaultSends(config.data?.networkMode);
  if (sends.length === 0) return null;
  const chains = config.data?.chains ?? [];
  return (
    <Card className="mt-4">
      <CardTitle>Recent vault sends</CardTitle>
      <p className="mt-1 text-sm text-muted">Kept only in this browser.</p>
      <ul className="mt-4 flex flex-col divide-y divide-line" data-testid="vault-sends">
        {sends.map((s) => {
          const chain = findChain(chains, s.chain);
          return (
            <li key={s.txHash} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <div className="min-w-0">
                <p className="text-fg">
                  <span className="font-mono">{formatAmount(s.amount)}</span> {s.asset} ·{" "}
                  {networkLabel(chain, s.chain)}
                </p>
                <p className="flex items-center gap-1 text-xs text-muted">
                  to <Address value={s.to} copy={false} /> · {formatDateTime(s.at)}
                </p>
              </div>
              {chain && <ExternalLink href={explorerTxUrl(chain, s.txHash)}>Explorer</ExternalLink>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function VaultAssetRow({
  row,
  network,
  currency,
}: {
  row: VaultBalanceRow;
  network: string;
  currency: string;
}) {
  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-card bg-surface p-4 lg:bg-soft",
        !row.available && "opacity-70",
      )}
      data-testid="vault-asset"
    >
      <AssetIcon symbol={row.asset} />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-fg">{row.name}</p>
        <p className="truncate text-sm text-muted">{network}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Badge>{row.asset}</Badge>
        {row.available ? (
          <>
            <span className="font-mono text-sm text-fg tabular-nums">{formatAmount(row.balance ?? "0")}</span>
            <span className="font-mono text-xs text-muted tabular-nums">
              {row.value === null ? "—" : formatFiat(row.value, currency)}
            </span>
          </>
        ) : (
          <span className="text-xs text-warning">Can't read right now</span>
        )}
      </div>
    </li>
  );
}

/** Small summary for the Home "Vault" tab. */
export function VaultHomeTab() {
  const vault = useVaultSession();
  const chains = useConfig().data?.chains ?? [];
  const currency = usePrefs((s) => s.currency);
  const balances = useVaultBalances(vault.accounts, currency, vault.status === "unlocked");

  if (vault.status !== "unlocked") {
    const text =
      vault.status === "locked"
        ? "Your vault is locked."
        : vault.status === "none"
          ? "Self-custody: keys only on this device. Create or import a wallet."
          : "The vault isn't available in this browser window.";
    return (
      <div className="glass flex flex-col items-center gap-3 rounded-card border border-line p-6 text-center lg:bg-soft">
        <Spot name="vault" />
        <p className="max-w-xs text-sm text-muted">{text}</p>
        <Link to="/vault" className={buttonClasses("secondary", "sm")}>
          {vault.status === "locked" ? "Unlock vault" : "Open vault"}
        </Link>
      </div>
    );
  }
  const rows = (balances.data?.assets ?? []).filter((r) => !r.available || !isZeroAmount(r.balance ?? "0"));
  return (
    <div className="flex flex-col gap-3">
      <p className="-mb-1 text-xs text-muted">
        Self-custody — keys only on this device. Total{" "}
        <span className="font-mono text-fg" data-testid="home-vault-total">
          {balances.data ? formatFiat(balances.data.total, currency) : "—"}
        </span>
      </p>
      {balances.isPending ? (
        <Skeleton className="h-20 rounded-card" />
      ) : rows.length === 0 ? (
        <div className="glass rounded-card border border-line lg:bg-soft">
          <EmptyState icon={<Spot name="coins" />} title="No funds in the vault yet">
            <Link to="/vault/receive" className="text-primary-strong hover:underline">
              Show my vault addresses
            </Link>
          </EmptyState>
        </div>
      ) : (
        <ul className="stagger flex flex-col gap-3">
          {rows.map((r) => (
            <VaultAssetRow
              key={`${r.chain}:${r.asset}`}
              row={r}
              currency={currency}
              network={networkLabel(findChain(chains, r.chain), r.chain)}
            />
          ))}
        </ul>
      )}
      <Link to="/vault" className={cn(buttonClasses("secondary", "sm"), "self-center")}>
        Open vault
      </Link>
    </div>
  );
}
